import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

export default function UserData() {
  const { t } = useTranslation();
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: '', email: '', age: '', weight: '', height: '', gender: '',
    goal: '', activityLevel: '', daysPerWeek: '', timePerSession: '',
    workoutLocation: '', experienceLevel: '', language: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        age: user.age?.toString() || '',
        weight: user.weight?.toString() || '',
        height: user.height?.toString() || '',
        gender: user.gender || '',
        goal: user.goal || '',
        activityLevel: user.activityLevel || '',
        daysPerWeek: user.daysPerWeek?.toString() || '',
        timePerSession: user.timePerSession?.toString() || '',
        workoutLocation: user.workoutLocation || '',
        experienceLevel: user.experienceLevel || '',
        language: user.language || 'en',
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await updateProfile({
        name: form.name,
        email: form.email,
        age: form.age ? Number(form.age) : undefined,
        weight: form.weight ? Number(form.weight) : undefined,
        height: form.height ? Number(form.height) : undefined,
        gender: form.gender || undefined,
        goal: form.goal || undefined,
        activityLevel: form.activityLevel || undefined,
        daysPerWeek: form.daysPerWeek ? Number(form.daysPerWeek) : undefined,
        timePerSession: form.timePerSession ? Number(form.timePerSession) : undefined,
        workoutLocation: form.workoutLocation || undefined,
        experienceLevel: form.experienceLevel || undefined,
        language: form.language || undefined,
      });
      setMessage({ type: 'success', text: t('userData.saveSuccess') });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || t('userData.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const selectClass = inputClass;

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('userData.title')}</h1>

      {message.text && (
        <div className={`p-3 rounded-lg mb-4 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('userData.personalInfo')}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t('userData.name')}</label>
              <input name="name" value={form.name} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{t('auth.email')}</label>
              <input name="email" value={form.email} onChange={handleChange} className={inputClass} type="email" />
            </div>
            <div>
              <label className={labelClass}>{t('userData.age')}</label>
              <input name="age" value={form.age} onChange={handleChange} className={inputClass} type="number" />
            </div>
            <div>
              <label className={labelClass}>{t('userData.gender')}</label>
              <select name="gender" value={form.gender} onChange={handleChange} className={selectClass}>
                <option value="">--</option>
                <option value="male">{t('common.male')}</option>
                <option value="female">{t('common.female')}</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('userData.weight')} (kg)</label>
              <input name="weight" value={form.weight} onChange={handleChange} className={inputClass} type="number" step="0.1" />
            </div>
            <div>
              <label className={labelClass}>{t('userData.height')} (cm)</label>
              <input name="height" value={form.height} onChange={handleChange} className={inputClass} type="number" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('userData.workoutSettings')}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t('userData.fitnessGoals')}</label>
              <select name="goal" value={form.goal} onChange={handleChange} className={selectClass}>
                <option value="">--</option>
                <option value="muscle_gain">{t('fitnessGoals.muscle_gain')}</option>
                <option value="weight_loss">{t('fitnessGoals.weight_loss')}</option>
                <option value="strength">{t('fitnessGoals.strength')}</option>
                <option value="endurance">{t('fitnessGoals.endurance')}</option>
                <option value="general_fitness">{t('fitnessGoals.general_fitness')}</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('userData.activityLevel')}</label>
              <select name="activityLevel" value={form.activityLevel} onChange={handleChange} className={selectClass}>
                <option value="">--</option>
                <option value="sedentary">{t('activityLevels.sedentary')}</option>
                <option value="lightly_active">{t('activityLevels.lightly_active')}</option>
                <option value="moderately_active">{t('activityLevels.moderately_active')}</option>
                <option value="very_active">{t('activityLevels.very_active')}</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('userData.daysPerWeek')}</label>
              <select name="daysPerWeek" value={form.daysPerWeek} onChange={handleChange} className={selectClass}>
                <option value="">--</option>
                {[1,2,3,4,5,6,7].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('userData.timePerSession')} (min)</label>
              <input name="timePerSession" value={form.timePerSession} onChange={handleChange} className={inputClass} type="number" />
            </div>
            <div>
              <label className={labelClass}>{t('userData.workoutLocation')}</label>
              <select name="workoutLocation" value={form.workoutLocation} onChange={handleChange} className={selectClass}>
                <option value="">--</option>
                <option value="gym">{t('workoutLocations.gym')}</option>
                <option value="home">{t('workoutLocations.home')}</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('userData.experienceLevel')}</label>
              <select name="experienceLevel" value={form.experienceLevel} onChange={handleChange} className={selectClass}>
                <option value="">--</option>
                <option value="beginner">{t('experienceLevels.beginner')}</option>
                <option value="intermediate">{t('experienceLevels.intermediate')}</option>
                <option value="advanced">{t('experienceLevels.advanced')}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('common.settings')}</h2>
          <div>
            <label className={labelClass}>{t('common.language')}</label>
            <select name="language" value={form.language} onChange={handleChange} className={selectClass}>
              <option value="en">English</option>
              <option value="pt-BR">Português (BR)</option>
              <option value="es">Español</option>
            </select>
          </div>
        </div>

        <button type="submit" disabled={saving} className="w-full py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors">
          {saving ? t('common.submitting') : t('userData.save')}
        </button>
      </form>
    </div>
  );
}
